import type { TimelineItem } from '@/types/timeline'
import type { ActiveTransition } from './canvas-transitions'
import { timelineToSourceFrames } from '@/features/export/deps/timeline'

export interface SourceTimeRamp {
  anchor: 'start' | 'end'
  slope: number
  rampStart: number
  rampEnd: number
}

export interface RenderTimelineSpan {
  from: number
  durationInFrames: number
  sourceStart?: number
  sourceTimeRamp?: SourceTimeRamp
}

interface TransitionParticipantFrameWindow {
  from: number
  durationInFrames: number
}

function isSourceTimedItem(item: TimelineItem): item is TimelineItem & {
  type: 'video' | 'audio' | 'composition'
  sourceStart?: number
  trimStart?: number
  offset?: number
  sourceFps?: number
  speed?: number
} {
  return item.type === 'video' || item.type === 'audio' || item.type === 'composition'
}

function getSourceTimedItemStart(
  item: TimelineItem & {
    sourceStart?: number
    trimStart?: number
    offset?: number
  },
): number {
  return item.sourceStart ?? item.trimStart ?? item.offset ?? 0
}

export function getItemRenderTimelineSpan(item: TimelineItem): RenderTimelineSpan {
  return {
    from: item.from,
    durationInFrames: item.durationInFrames,
    ...(isSourceTimedItem(item) ? { sourceStart: getSourceTimedItemStart(item) } : {}),
  }
}

export function getRenderTimelineSourceStart(
  item: TimelineItem,
  span?: RenderTimelineSpan,
): number {
  if (!isSourceTimedItem(item)) {
    return 0
  }
  return span?.sourceStart ?? getSourceTimedItemStart(item)
}

export function applyRenderTimelineSpan<TItem extends TimelineItem>(
  item: TItem,
  span?: RenderTimelineSpan,
): TItem {
  if (!span) {
    return item
  }

  const hasSameTimelineWindow =
    item.from === span.from && item.durationInFrames === span.durationInFrames
  const nextSourceStart = getRenderTimelineSourceStart(item, span)
  const currentSourceStart = isSourceTimedItem(item) ? getSourceTimedItemStart(item) : undefined
  const hasSameSourceAnchor = currentSourceStart === nextSourceStart

  if (hasSameTimelineWindow && hasSameSourceAnchor) {
    return item
  }

  return {
    ...item,
    from: span.from,
    durationInFrames: span.durationInFrames,
    ...(isSourceTimedItem(item) ? { sourceStart: nextSourceStart } : {}),
  }
}

function resolveTransitionParticipantFrameWindow<TItem extends TimelineItem>(
  clip: TItem,
  activeTransition: Pick<ActiveTransition, 'transitionStart' | 'transitionEnd'>,
): TransitionParticipantFrameWindow {
  const beforeFrames = Math.max(0, clip.from - activeTransition.transitionStart)
  const clipEnd = clip.from + clip.durationInFrames
  const afterFrames = Math.max(0, activeTransition.transitionEnd - clipEnd)

  return {
    from: clip.from - beforeFrames,
    durationInFrames: clip.durationInFrames + beforeFrames + afterFrames,
  }
}

function getTransitionParticipantSourceStart<TItem extends TimelineItem>(
  clip: TItem,
  transitionWindow: TransitionParticipantFrameWindow,
  fps: number,
): number | undefined {
  if (!isSourceTimedItem(clip)) {
    return undefined
  }

  const beforeFrames = Math.max(0, clip.from - transitionWindow.from)
  if (beforeFrames <= 0) {
    return getSourceTimedItemStart(clip)
  }

  const sourceStart = getSourceTimedItemStart(clip)
  const speed = clip.speed ?? 1
  const sourceFps = clip.sourceFps ?? fps
  const prerollSourceFrames = timelineToSourceFrames(beforeFrames, speed, fps, sourceFps)
  return Math.max(0, sourceStart - prerollSourceFrames)
}

export function resolveTransitionRenderTimelineSpan<TItem extends TimelineItem>(
  clip: TItem,
  activeTransition: Pick<ActiveTransition, 'transitionStart' | 'transitionEnd'>,
  fps: number,
  sourceTimeRamp?: SourceTimeRamp,
): RenderTimelineSpan {
  const transitionWindow = resolveTransitionParticipantFrameWindow(clip, activeTransition)
  const sourceStart = getTransitionParticipantSourceStart(clip, transitionWindow, fps)
  return {
    from: transitionWindow.from,
    durationInFrames: transitionWindow.durationInFrames,
    ...(sourceStart !== undefined ? { sourceStart } : {}),
    ...(sourceTimeRamp ? { sourceTimeRamp } : {}),
  }
}

const A_A_DEFAULT_RAMP_SLOPE = 0.5

export function isAAContinuousSplit(
  leftClip: TimelineItem,
  rightClip: TimelineItem,
  fps: number,
): boolean {
  if (leftClip.type !== 'video' || rightClip.type !== 'video') return false
  if (!leftClip.mediaId || leftClip.mediaId !== rightClip.mediaId) return false
  if (leftClip.isReversed || rightClip.isReversed) return false

  const leftSourceStart = getSourceTimedItemStart(leftClip)
  const rightSourceStart = getSourceTimedItemStart(rightClip)
  const speed = leftClip.speed ?? 1
  const sourceFps = leftClip.sourceFps ?? fps
  const leftSourceFrames = timelineToSourceFrames(
    leftClip.durationInFrames,
    speed,
    fps,
    sourceFps,
  )
  const leftNaturalSourceEnd = leftSourceStart + leftSourceFrames

  return Math.abs(leftNaturalSourceEnd - rightSourceStart) < 0.5
}

export function resolveAATransitionRamps(
  leftClip: TimelineItem,
  rightClip: TimelineItem,
  activeTransition: Pick<ActiveTransition, 'transitionStart' | 'transitionEnd'>,
  fps: number,
): { left: SourceTimeRamp; right: SourceTimeRamp } | null {
  if (!isAAContinuousSplit(leftClip, rightClip, fps)) return null

  const common = {
    slope: A_A_DEFAULT_RAMP_SLOPE,
    rampStart: activeTransition.transitionStart,
    rampEnd: activeTransition.transitionEnd,
  }

  return {
    left: { anchor: 'start', ...common },
    right: { anchor: 'end', ...common },
  }
}

export function getSourceFrameRampOffset(ramp: SourceTimeRamp, frame: number): number {
  if (frame < ramp.rampStart || frame > ramp.rampEnd) return 0
  const delta = ramp.anchor === 'start' ? frame - ramp.rampStart : frame - ramp.rampEnd
  return ramp.slope * delta
}
